<template>
  <the-dialog-box
    ref="dialogBox"
    :submit="submit"
    submit-btn-text="Create"
    :btn-loading="isCheckingSealedUnitCount"
    :submit-btn-disabled="isCheckingSealedUnitCount || isSealedUnitOverbooked"
    :full-screen="true"
    :persistent="true"
    :warning-text="'*Red ones are still to be created'"
  >
    <template slot="header">
      <span>Create Remake PO</span>
    </template>
    <template slot="body">
      <v-row>
        <v-col
          cols="12"
          md="3"
        >
          <v-dialog
            v-model="dateMenuGlassRequiredByDate"
            max-width="350px"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="glassRequiredByDate"
                readonly
                append-icon="mdi-calendar"
                v-bind="attrs"
                :error="isSealedUnitOverbooked"
                :error-messages="sealedUnitOverbookedErrorMessages"
                :loading="isCheckingSealedUnitCount"
                :disabled="loading"
                v-on="on"
              >
                <template slot="label">
                  Glass Required By<RedAsterisk />
                </template>
              </v-text-field>
            </template>
            <v-card>
              <v-card-title class="business-date-picker-dialog-title">
                Glass Required By Date
              </v-card-title>
              <v-card-text class="pt-6">
                <v-row>
                  <v-col>
                    <business-date-picker
                      v-model="glassRequiredByDate"
                      no-title
                      scrollable
                      :min="minGlassRequiredByDate"
                      @input="onGlassRequiredByDateChange(glassRequiredByDate)"
                    >
                      <sealed-unit-overbooking-status-chip
                        class="mx-4 mb-2"
                        :message="sealedUnitStatusMessage"
                        :checking="isCheckingSealedUnitCount"
                        :overbooked="isSealedUnitOverbooked"
                        :checked="isSealedUnitCountChecked"
                      />
                    </business-date-picker>
                  </v-col>
                </v-row>
              </v-card-text>
              <div class="business-date-picker-dialog-action-divider" />
              <v-card-actions>
                <v-spacer />
                <v-btn
                  text
                  @click="dateMenuGlassRequiredByDate = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  @click="clearGlassRequiredByDate"
                >
                  Clear
                </v-btn>
                <v-btn
                  text
                  :loading="isCheckingSealedUnitCount"
                  :disabled="isSealedUnitOverbooked || isCheckingSealedUnitCount"
                  @click="dateMenuGlassRequiredByDate = false"
                >
                  Apply
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="pt-0"
        >
          <v-card
            elevation="0"
            outlined
          >
            <v-data-table
              :headers="headers"
              :items="items"
              hide-default-footer
              :items-per-page="-1"
              :loading="loading"
              dense
            >
              <template #[`item.inventory_class_id`]="{ item }">
                <v-chip
                  small
                  :color="item && item.class ? item.class.color : 'black'"
                  text-color="white"
                >
                  {{ item && item.class ? item.class.name : '' }}
                </v-chip>
              </template>
              <template #[`item.batch_number`]="{ item }">
                <span
                  style="font-size: 14px !important; color: red;"
                  class="font-weight-bold"
                >

                  {{ item.batch_number ? item.batch_number : '' }}
                </span>
              </template>
              <template #[`item.po_number`]="{ item }">
                {{ item.po_number }}
              </template>
              <template #[`item.g_code`]="{ item }">
                {{ item.g_code }}
              </template>
              <template #[`item.glass_thickness`]="{ item }">
                {{ item.glass_thickness }}
              </template>
              <template #[`item.total_glass`]="{ item }">
                {{ item.total_glass }}
              </template>
              <template #[`item.job_code`]="{ item }">
                {{ item.job && item.job.code ? item.job.code : '-' }}
              </template>
              <template #[`item.job_name`]="{ item }">
                {{ item.job && item.job.name ? item.job.name : '-' }}
              </template>
              <template #[`item.remake_reason_id`]="{ item }">
                <v-select
                  v-model="item.remake_reason_id"
                  :items="remakeReasons"
                  label="Remake Main Category"
                  item-text="name"
                  item-value="id"
                  :rules="[(v) => !!v || 'Remake Main Category is required']"
                />
              </template>
              <template #[`item.remake_sub_reason_id`]="{ item }">
                <v-select
                  v-model="item.remake_sub_reason_id"
                  :items="findSubReasons(item.remake_reason_id)"
                  label="Remake Sub Category"
                  item-text="name"
                  item-value="id"
                  :disabled="!item.remake_reason_id"
                  :rules="[(v) => !!v || 'Remake Sub Category is required']"
                />
              </template>
              <template #[`item.status`]="{ }">
                <v-chip
                  small
                  color="error"
                  text-color="white"
                >
                  TO BE CREATED
                </v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </the-dialog-box>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import { addBusinessDays } from '@/helpers/BusinessDateHelper';
import BusinessDatePicker from '@/components/elements/BusinessDatePicker.vue';
import SealedUnitOverbookingStatusChip from '@/components/elements/SealedUnitOverbookingStatusChip.vue';

export default {
  components: {
    BusinessDatePicker,
    SealedUnitOverbookingStatusChip,
  },
  data: () => ({
    groupedBatches: [],
    items: [],
    selectedIds: [],
    inventoryClassId: null,
    remakeReasons: [],
    headers: [
      { text: 'Class', value: 'inventory_class_id' },
      { text: 'WO#', value: 'job_code' },
      { text: 'Project', value: 'job_name' },
      { text: 'PO#', value: 'po_number' },
      { text: 'Batch#', value: 'batch_number' },
      { text: 'Glass Code', value: 'g_code' },
      { text: 'Glass Thickness', value: 'glass_thickness' },
      { text: 'Glass Qty', value: 'total_glass' },
      { text: 'Max Glass Qty', value: 'max_total_glass' },
      { text: 'Remake Main Category', value: 'remake_reason_id', width: '10%' },
      { text: 'Remake Sub Category', value: 'remake_sub_reason_id', width: '10%' },
      { text: 'Status', value: 'status', align: 'center' },
    ],
    loading: false,
    isRemake: false,
    remakeSubReasons: [],
    remakeSubReasonsAll: [],
    dateMenuGlassRequiredByDate: false,
    glassRequiredByDate: null,
    isCheckingSealedUnitCount: false,
    isSealedUnitOverbooked: false,
    isSealedUnitCountChecked: false,
    sealedUnitOverbookedMessage: '',
    maximumSealedUnitsAllowedPerDay: 300,
    glassTypeIdSealed: 2,
  }),
  computed: {
    ...mapState({
      holidaysDateToNameMap: (state) => state.holidaysDateToNameMap,
    }),
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
    minGlassRequiredByDate() {
      return addBusinessDays(moment().format('YYYY-MM-DD'), 4, this.holidaysDateToNameMap);
    },
    sealedUnitOverbookedErrorMessages() {
      if (!this.isSealedUnitOverbooked) return [];
      return ['Sealed Units overbooked for this date'];
    },
    sealedUnitStatusMessage() {
      if (this.isCheckingSealedUnitCount) return 'Checking Sealed Units...';
      if (this.isSealedUnitOverbooked) return this.sealedUnitOverbookedMessage;
      if (this.isSealedUnitCountChecked) return 'Sealed Unit capacity OK';
      return null;
    },
  },
  watch: {
    'item.remake_reason_id': {
      handler() {
        this.item.remake_sub_reason_id = null;
      },
      deep: true,
    },
  },
  methods: {

    async getGroupedBatches() {
      if (!this.selectedIds || this.selectedIds.length === 0) return;
      this.loading = true;
      const submitModel = {
        selected_ids: this.selectedIds,
        inventory_class_id: this.inventoryClassId,
      };
      const res = await this.$api.glassBatch.store(submitModel, 'grouped-manual');
      if (!res) return;
      this.groupedBatches = res;

      if (this.isRemake) {
        const date = new Date();
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);
        const formattedDate = day + month + year;
        this.groupedBatches.forEach((x) => {
          // eslint-disable-next-line no-param-reassign
          x.po_number = `${formattedDate}R`;
        });
      }
      this.loading = false;
    },

    async get() {
      if (!this.selectedIds || this.selectedIds.length === 0) return;
      this.loading = true;
      const submitModel = {
        filters: {
          selected_ids: this.selectedIds,
          is_all: 1,
        },
      };

      const res = await this.$api.glass.readGlass(submitModel);
      if (!res) {
        this.loading = false;
        return;
      }

      this.items = res.data;
      this.setGlassRequiredByDateForItems(this.glassRequiredByDate);
      this.loading = false;
    },

    async submit() {
      if (this.items.length === 0) {
        this.$bus.$emit('showError', 'There are no Glass items to add to a Batch!');
        return false;
      }
      if (!this.glassRequiredByDate) {
        this.$bus.$emit('showError', 'Glass Required By Date is required.');
        return false;
      }
      if (this.isCheckingSealedUnitCount) {
        this.$bus.$emit('showError', 'Checking sealed unit count, please wait.');
        return false;
      }
      if (this.isSealedUnitOverbooked) {
        this.$bus.$emit('showError', 'Sealed unit is overbooked, please select another Glass Required By Date.');
        return false;
      }

      this.setGlassRequiredByDateForItems(this.glassRequiredByDate);

      this.$bus.$emit('loading');

      const res = await this.$api.glassPurchaseOrderItem.store(this.items, 'store-remake');
      this.$bus.$emit('loading-stop');

      if (!res) {
        this.$bus.$emit('showError');
        return false;
      }

      this.$emit('success');
      return res;
    },

    open(selectedIds, inventoryClassId, isRemake) {
      this.$refs.dialogBox.open();
      this.groupedBatches = [];
      this.items = [];
      this.selectedIds = selectedIds;
      this.inventoryClassId = inventoryClassId;
      this.isRemake = isRemake;
      this.dateMenuGlassRequiredByDate = false;
      this.glassRequiredByDate = null;
      this.resetSealedUnitOverbookedValidation();
      this.get();
      this.getReasons();
      this.getRemakeSubReasons();
    },

    async getReasons() {
      const res = await this.$api.glassRemakeReason.get();
      if (!res) return;
      const brokenId = 11;
      const defectiveID = 13;
      const missingId = 1;
      const productionIssueId = 16;
      this.remakeReasons = res.filter(
        (x) => x.id === brokenId || x.id === defectiveID || x.id === missingId || x.id === productionIssueId,
      );
    },

    async getRemakeSubReasons() {
      this.remakeSubReasonsAll = await this.$api.glassRemakeSubReason.get();
    },

    findSubReasons(id) {
      return this.remakeSubReasonsAll.filter((x) => x.glass_remake_reason_id === id);
    },

    onGlassRequiredByDateChange(date) {
      this.setGlassRequiredByDateForItems(date);
      this.checkSealedUnitOverbooking(date);
    },

    clearGlassRequiredByDate() {
      this.glassRequiredByDate = null;
      this.dateMenuGlassRequiredByDate = false;
      this.setGlassRequiredByDateForItems(null);
      this.resetSealedUnitOverbookedValidation();
    },

    setGlassRequiredByDateForItems(date) {
      this.items.forEach((item) => {
        this.$set(item, 'required_date', date);
        this.$set(item, 'glass_required_by', date);
      });
    },

    async checkSealedUnitOverbooking(date) {
      this.resetSealedUnitOverbookedValidation();
      if (!this.businessRulesGetBoolean('Sealed Unit Overbooking Check')) return true;
      if (!date) return true;

      const pendingSealedUnitCount = this.getSealedUnitQuantity(this.items);
      if (pendingSealedUnitCount <= 0) return true;

      this.isCheckingSealedUnitCount = true;
      try {
        const overbookingCheck = await this.$api.glass.store({
          glass_required_by: date,
        }, 'sealed-unit-overbooking-check');

        if (this.glassRequiredByDate !== date) return true;

        if (!overbookingCheck || overbookingCheck.status === 'error') {
          this.$bus.$emit('showError', overbookingCheck && overbookingCheck.message ? overbookingCheck.message : 'Unable to check sealed unit count. Please try again.');
          return false;
        }

        const scheduledSealedUnitCount = parseInt(overbookingCheck.scheduled_sealed_unit_count, 10) || 0;
        const totalSealedUnitCount = scheduledSealedUnitCount + pendingSealedUnitCount;
        const maximumSealedUnitsAllowedPerDay = parseInt(overbookingCheck.maximum_sealed_units_allowed_per_day, 10) || this.maximumSealedUnitsAllowedPerDay;

        this.maximumSealedUnitsAllowedPerDay = maximumSealedUnitsAllowedPerDay;
        this.isSealedUnitCountChecked = true;

        if (totalSealedUnitCount > maximumSealedUnitsAllowedPerDay) {
          this.isSealedUnitOverbooked = true;
          const humanReadableDate = moment(date).format('MMMM Do, YYYY');
          this.sealedUnitOverbookedMessage = `Sorry, there will be ${totalSealedUnitCount} Sealed Units scheduled for ${humanReadableDate}. The maximum allowed is ${maximumSealedUnitsAllowedPerDay}. Please select a different date.`;
          this.$bus.$emit('showError', this.sealedUnitOverbookedMessage);
          return false;
        }

        return true;
      } finally {
        if (this.glassRequiredByDate === date) {
          this.isCheckingSealedUnitCount = false;
        }
      }
    },

    getSealedUnitQuantity(items) {
      return items.reduce((total, item) => {
        if (!this.isSealedUnitGlassPOItem(item)) return total;
        return total + 1;
      }, 0);
    },

    isSealedUnitGlassPOItem(item) {
      if (!item) return false;
      const glassCodeTypeId = item.glass ? item.glass.glass_code_type_id : item.glass_code_type_id;
      return parseInt(glassCodeTypeId, 10) === this.glassTypeIdSealed;
    },

    resetSealedUnitOverbookedValidation() {
      this.isSealedUnitOverbooked = false;
      this.sealedUnitOverbookedMessage = '';
      this.isCheckingSealedUnitCount = false;
      this.isSealedUnitCountChecked = false;
    },
  },
};
</script>

<style scoped>
::v-deep .v-data-table__wrapper {
    overflow-x: hidden !important;
}

.business-date-picker-dialog-title {
  background-color: #323232;
  color: #FFFFFF;
}

.business-date-picker-dialog-action-divider {
  width: calc(100% - 48px);
  margin: 0 auto;
  border-top: 1px solid #E0E0E0;
}

</style>
